import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, PUBLIC_HOST } from "@/lib/api";
import { ArrowLeft, Share2 } from "lucide-react";
import { toast } from "sonner";
import HotLotBadge from "@/components/HotLotBadge";

/**
 * Public Frenzy Moment share page (`/moment/:id`).
 *
 * Vertical 9:16 hero card optimized for screenshot / screen-record so
 * sellers can drop it on TikTok/Instagram/X. The frenzy badge animates
 * exactly like the in-stream pulse, the lot image fills the top half,
 * and the bottom rail shows winning bid + bid velocity + recent
 * chat reactions.
 */
export default function FrenzyMoment() {
  const { id } = useParams();
  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/frenzy-moments/${id}`);
        if (!cancelled) setMoment(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Moment not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = moment?.listing_title
      ? `🔥 FRENZY on ${moment.listing_title} · Bidorah`
      : "🔥 FRENZY · Bidorah";
    if (navigator.share) {
      try {
        await navigator.share({ url, title });
        return;
      } catch (_e) {
        /* user cancelled — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch (_e) {
      toast.error("Couldn't copy");
    }
  };

  const peakLevel = useMemo(
    () => moment?.peak_level || moment?.opened_level || "hot",
    [moment]
  );

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-zinc-400">
        <div className="animate-pulse text-sm">Loading frenzy…</div>
      </div>
    );
  }
  if (err || !moment) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 text-zinc-300 text-center px-6">
        <div>
          <div className="text-2xl font-black mb-2">No frenzy here</div>
          <div className="text-sm text-zinc-500 mb-6">{err || "That moment may have expired."}</div>
          <Link
            to="/live"
            className="inline-flex items-center gap-2 rounded-full bg-[#1D78AB] text-white px-5 py-2 font-bold"
            data-testid="frenzy-moment-back-live"
          >
            Watch live shows
          </Link>
        </div>
      </div>
    );
  }

  const img = moment.listing_image ? `${PUBLIC_HOST}/api/files/${moment.listing_image}` : null;
  const winning = moment.winning_amount;
  const winner = moment.winner_name;
  const peak = moment.peak_bids_5s || 0;
  const totalBids = moment.total_bids || 0;
  const bids = (moment.bid_snapshot || []).slice(-5);
  const chats = (moment.chat_snapshot || []).slice(-4);

  return (
    <div
      className="min-h-screen bg-zinc-950 text-white"
      data-testid="frenzy-moment-page"
    >
      <div className="max-w-md mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <Link
            to="/live"
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm"
            data-testid="frenzy-moment-back"
          >
            <ArrowLeft size={16} /> Live
          </Link>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm font-semibold"
            data-testid="frenzy-moment-share"
          >
            <Share2 size={14} /> Share
          </button>
        </div>

        {/* Vertical hero — 9:16 ratio so a screenshot is already framed
            for TikTok / Instagram Reels / X verticals. */}
        <div
          className={`relative aspect-[9/16] w-full rounded-3xl overflow-hidden border border-white/10 ${peakLevel === "frenzy" ? "hot-lot-card-frenzy" : "hot-lot-card-hot"}`}
          data-testid="frenzy-moment-hero"
        >
          {img ? (
            <img
              src={img}
              alt={moment.listing_title || ""}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-rose-600 via-orange-500 to-amber-400" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85" />

          {/* Top headline */}
          <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
            <div>
              <HotLotBadge level={peakLevel} bids5s={peak} />
              <div className="mt-2 text-2xl font-black tracking-tight leading-tight max-w-[260px]">
                {moment.listing_title || "Live auction"}
              </div>
            </div>
          </div>

          {/* Bottom stats */}
          <div className="absolute bottom-0 inset-x-0 p-5 space-y-3">
            {winning != null && winner && (
              <div data-testid="frenzy-moment-winner" className="bg-black/60 backdrop-blur rounded-2xl px-4 py-3 border border-white/10">
                <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">
                  Hammered
                </div>
                <div className="text-3xl font-black tracking-tight text-white">
                  ${Number(winning).toFixed(0)}
                </div>
                <div className="text-sm text-zinc-300">
                  to <span className="font-bold text-white">@{winner}</span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-black/60 backdrop-blur rounded-xl px-3 py-2 border border-white/10">
                <div className="text-[10px] uppercase text-zinc-400 tracking-widest">Peak velocity</div>
                <div className="text-lg font-black text-white">{peak}<span className="text-xs text-zinc-400">/5s</span></div>
              </div>
              <div className="bg-black/60 backdrop-blur rounded-xl px-3 py-2 border border-white/10">
                <div className="text-[10px] uppercase text-zinc-400 tracking-widest">Total bids</div>
                <div className="text-lg font-black text-white">{totalBids}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent bids / chat strips */}
        {bids.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
              Final bids
            </div>
            <div className="space-y-1">
              {bids.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-zinc-900/70 border border-white/5 rounded-lg px-3 py-2 text-sm"
                  data-testid={`frenzy-moment-bid-${i}`}
                >
                  <span className="text-zinc-300">@{b.user_name || "—"}</span>
                  <span className="font-bold text-white">${Number(b.amount || 0).toFixed(0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {chats.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
              Crowd reaction
            </div>
            <div className="space-y-1">
              {chats.map((c, i) => (
                <div
                  key={i}
                  className="bg-zinc-900/70 border border-white/5 rounded-lg px-3 py-2 text-sm"
                  data-testid={`frenzy-moment-chat-${i}`}
                >
                  <span className="text-zinc-400 mr-2 font-bold">@{c.user_name || "—"}</span>
                  <span className="text-zinc-200">{c.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link
          to={`/stream/${moment.stream_id}`}
          className="mt-5 block text-center bg-[#1D78AB] hover:bg-[#176290] text-white font-black rounded-full py-3 tracking-tight"
          data-testid="frenzy-moment-go-stream"
        >
          Watch this seller live
        </Link>
      </div>
    </div>
  );
}
